<!--Author: Michał Pawłowski-->
<template>
  <div class="common-table-card shadow-sm border rounded bg-white">


    <transition name="fade">
      <div v-if="showCopyTooltipAlert" class="copy-tooltip-wrapper">
        <b-alert :model-value="true" dismissible variant="success"
                 class="shadow m-0 d-flex align-items-center bg-white opacity-100">
          <i class="fas fa-check-circle me-2"></i>
          <div>
            <strong>Skopiowano</strong><br>
            <span class="small text-truncate d-inline-block" style="max-width: 200px;">{{ copyTooltipContent }}</span>
          </div>
        </b-alert>
      </div>
    </transition>

    <div
        class="table-toolbar bg-light border-bottom p-2 d-flex flex-wrap justify-content-between align-items-center gap-2">

      <div class="flex-grow-1 d-flex align-items-center overflow-hidden" style="min-height: 38px;">
        <transition name="slide-fade">
          <div v-if="itemsMassActionsEnabled" class="d-flex align-items-center w-100">
            <span class="text-uppercase fw-bold text-muted small me-3"
                  style="font-size: 0.75rem; letter-spacing: 0.5px;">Akcje:</span>
            <div class="d-flex flex-wrap gap-1">
              <button
                  v-for="(action, index) in localTable.massActions"
                  :key="index"
                  :class="getMassActionClass(action)"
                  @click="triggerMassAction(action)"
                  :disabled="!action.enabled"
                  :title="action.text"
              >
                <i :class="[action.icon, 'me-1']"/>
                {{ action.text }}
              </button>
            </div>
          </div>
        </transition>
      </div>

      <transition name="fade">
        <div v-if="selectedItems.length > 0" class="d-flex align-items-center text-muted border-end pe-3 me-1 h-100">
          <span class="small fw-semibold" style="font-size: 0.75rem; letter-spacing: 0.5px;">
            Zaznaczono: <span class="text-primary">{{ selectedItems.length }}</span>
          </span>
        </div>
      </transition>

      <slot name="table-related-buttons"></slot>
      <button class="d-none d-sm-inline-block btn btn-sm btn-outline-secondary shadow-sm" @click="showColumnManager = true" title="Zarządzaj kolumnami">
        <i class="fas fa-columns"></i>
      </button>


      <TableColumnManagerModal
          v-model:showModal="showColumnManager"
          :allHeaders="localTable.headers"
          :currentVisibleKeys="visibleHeaderKeys"
          :exportEnabled="exportEnabled"
          :exportDataKey="exportDataKey"
          @onApply="updateVisibleColumnsWithEmit"
          @onExportTable="exportTableData"
      />



      <div v-if="showPaginationTop" class="pagination-container">
        <b-pagination
            v-model="changedPage"
            size="sm"
            :total-rows="totalRows"
            :per-page="perPage"
            class="m-0"
            aria-controls="tableEl"
            :first-text="changedPage != 1 ? '1' : '||'"
            :last-text="totalPages > 1 ? totalPages.toString() : '||'"
        />

      </div>
    </div>

    <div class="table-wrapper"
         :style="{
       '--ct-sticky-top': stickyHeaderTop + 'px',
       '--ct-sticky-bottom': stickyScrollbarBottom + 'px'
     }"
    >

    <div
        class="table-responsive custom-scrollbar table-section table-scroll"
        ref="tableScroll"
        @scroll="syncScrollFromTable"
        @dblclick="handleContainerClick"
        @keydown="handleTableKeydown"
        @click="focusTableContainer"
    >
      <table id="tableEl"  ref="tableEl" class="table table-sm table-bordered table-hover mb-0 modern-table">
        <thead class="bg-light text-secondary z-2">
        <tr>
          <th v-show="showCheckboxColumn === true" class="text-center align-middle p-2 border-bottom"
              style="width: 40px; background-color: #f8f9fa;">
            <div class="custom-control custom-checkbox d-flex justify-content-center">
              <input
                  v-if="multiselect === true"
                  type="checkbox"
                  v-model="selectAllItems"
                  @change="selectItem"
                  class="form-check-input"
                  style="cursor: pointer;"
              />
            </div>
          </th>

          <th
              v-for="(header, index) in availableHeaders"
              :key="index"
              :id="buildTableElementId('header-id-', header.key, '', '')"
              :ref="buildTableElementId('header-id-', header.key, '', '')"
              :style="getHeaderStyle(header)"
              class="align-middle p-2 border-bottom user-select-none"
              style="background-color: #f8f9fa; cursor: pointer;"
              @click="sortBy(header)"
          >
            <div class="d-flex align-items-center justify-content-betweenw-100 overflow-hidden">
              <span class="fw-semibold small text-uppercasetext-truncate">{{ header.text }}</span>
              <span v-if="header.sortable" class="sort-indicators ms-2 d-flex flex-column lh-1 text-muted flex-shrink-0"
                    style="font-size: 8px;">
                   <i class="fas fa-caret-up" :class="{'text-dark': isSortedAsc(header.key)}"></i>
                   <i class="fas fa-caret-down" :class="{'text-dark': isSortedDesc(header.key)}"></i>
                </span>
            </div>
          </th>
        </tr>
        </thead>

        <tbody v-if="visibleDataItemsCount === 0">
        <tr>
          <td :colspan="availableHeaders.length + (showCheckboxColumn ? 1 : 0)" class="text-center p-4 text-muted">
            <div class="d-flex flex-column align-items-center gap-2">
              <i class="fas fa-table fa-2x opacity-25"></i>
              <span>Brak danych do wyświetlenia</span>
            </div>
          </td>
        </tr>
        </tbody>

        <tbody v-else class="bg-white">
        <tr
            v-for="(row,row_index) in tableData"
            :key="row[localRowIdentifier]"
            :class="getRowClass(row)"
            :id="buildTableRowRef(row[localRowIdentifier])"
            :ref="buildTableRowRef(row[localRowIdentifier])"
            @click="handleRowClick(row)"
            class="align-middle"
        >
          <td v-show="showCheckboxColumn" class="text-center align-middle p-0 position-relative"
              :id="buildTableColRef(row_index, 'checkbox')"
              :ref="buildTableColRef(row_index, 'checkbox')"
              @click.stop
          >
            <div v-if="selectedItems.includes(row[localRowIdentifier])" class="row-active-indicator"></div>
            <input
                v-if="!row.generatedEmptyRow"
                type="checkbox"
                :value="row[localRowIdentifier]"
                v-model="selectedItems"
                @change="triggerCheckAction"
                :disabled="isCheckboxDisabled(row)"
                class="form-check-input mt-0"
                style="cursor: pointer;"
            />
          </td>

          <td
              v-for="(header, col_index) in availableHeaders"
              :key="col_index"
              :style="header.style"
              class="px-2 py-1 text-nowrap position-relative"
              @dblclick.stop="handleCellDoubleClick($event, row[header.key], header)"
              @mouseenter="onCellMouseEnter"
              @mouseleave="onCellMouseLeave"
          >

            <div class="cell-content">
              <div v-if="header.type === 'text' || header.type === 'number'"
                   :class="header.type === 'number' ? 'text-end font-monospace col-number' : 'text-start col-text'"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header,row)"
                   :title="row.tooltip">
                {{ !row.generatedEmptyRow ? row[header.key] : '&nbsp;' }}
              </div>
              <div v-if="header.type === 'currency'"
                   :class="'text-end font-monospace col-currency'"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header,row)"
                   :title="row.tooltip">

                {{ !row.generatedEmptyRow ? formatCurrency(row[header.key]) : '' }}

              </div>

              <div v-if="header.type === 'float'"
                   :class="'text-end font-monospace col-number'"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header,row)"
                   :title="row.tooltip">

                {{ row[header.key] != null && !row.generatedEmptyRow ? parseFloat(row[header.key]).toFixed(2) : '' }}

              </div>

              <div v-if="header.type === 'progress'"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header, row)"
                   class="d-flex align-items-center h-100"
                   :title="(row[header.key] || 0) + '%'"
              >

                  <div  v-if="!row.generatedEmptyRow" class="progress flex-grow-1 shadow-sm"
                       style="height: 8px; background-color: #e9ecef; border-radius: 4px;">
                    <div class="progress-bar rounded"
                         role="progressbar"
                         :style="{
            width: Math.min(row[header.key] || 0, 100) + '%',
            backgroundColor: getProgressColor(row[header.key], header)
         }"
                         :aria-valuenow="row[header.key] || 0"
                         aria-valuemin="0"
                         aria-valuemax="100">
                    </div>
                  </div>

                  <span  v-if="!row.generatedEmptyRow" class="ms-2 small fw-bold text-muted"
                        style="min-width: 35px; text-align: right; font-size: 11px;">
                  {{ row[header.key] || 0 }}%
                </span>








              </div>


              <div v-if="header.type === 'date' || header.type === 'datetime'" class="text-muted small col-date"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header,row)"
              >
                <div v-if="!row.generatedEmptyRow" class="d-flex align-items-center gap-1">
                  <i class="far fa-calendar-alt me-1 opacity-50"></i>
                  {{
                    header.type === 'date' ? getDate(row[header.key], header.isEpochMillis) : getDateTime(row[header.key], header.isEpochMillis)
                  }}

                </div>

              </div>

              <div v-if="header.type === 'boolean'" class="text-center"
                   :id="buildTableColRef(row_index, col_index)"
                   :ref="buildTableColRef(row_index, col_index)"
                   :style="getCellStyle(header,row)"
              >
                    <span v-if="!row.generatedEmptyRow"  class="badge rounded-pill fw-normal"
                          :class="row[header.key] ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-danger-subtle text-danger border border-danger-subtle'">
                      {{ row[header.key] ? "Tak" : "Nie" }}
                    </span>
              </div>


              <div
                  v-if="header.type === 'button'"
                  :id="buildTableColRef(row_index, col_index)"
                  :ref="buildTableColRef(row_index, col_index)"
                  :class="getCellClass(header,row)"
                  :style="getCellStyle(header,row)"
              >
                <div v-if="!row.generatedEmptyRow">
                  <i :class="row[header.key]?.icon || header.icon"/>

                  <button

                      :id="buildTableColElementRef(row_index, col_index, '-btn')"
                      :ref="buildTableColElementRef(row_index, col_index, '-btn')"

                      :class="row[header.key]?.class || header.class"

                      :disabled="row[header.key]?.disabled !== undefined ? row[header.key]?.disabled : false"

                      @click="triggerRowAction({action: header.action}, row)"
                  >
                    {{ row[header.key]?.text || header.text }}
                  </button>

                </div>

              </div>
              <div
                  v-if="header.type === 'icon'"
                  :id="buildTableColRef(row_index, col_index)"
                  :ref="buildTableColRef(row_index, col_index)"
                  :class="getCellClass(header,row)"
                  :style="getCellStyle(header,row)"
              >
                <i v-if="!row.generatedEmptyRow" :class="row[header.key]"/>
              </div>
              <div
                  v-if="header.type === 'array'"
                  :id="buildTableColRef(row_index, col_index)"
                  :ref="buildTableColRef(row_index, col_index)"
                  :class="getCellClass(header,row)"
                  :style="getCellStyle(header,row)"
              >
                <!--                TODO: add some list component within the cell-->

              </div>


              <div v-if="showRowActions === true && header.type === 'actions'" class="d-flex gap-1">
                <div v-if="!row.generatedEmptyRow" class="d-flex align-items-center gap-1">
                     <button v-for="(action, idx) in header.actions" :key="'h-'+idx"
                             :class="getRowButtonClass(action)"
                             :style="getCellStyle(header,row)"
                             :title="action.tooltip" :disabled="!action.enabled"
                             @click="triggerRowAction(action, row,
                         buildTableColElementRef(row_index, col_index,'-action-btn-' + action.action))"
                             :id="
                    buildTableColElementRef(
                      row_index,
                      col_index,
                      '-action-btn-' + action.action
                    )
                  "
                             :ref="
                    buildTableColElementRef(
                      row_index,
                      col_index,
                      '-action-btn-' + action.action
                    )
                  "


                     >
                    <i :class="action.icon"></i>
                       {{action.text}}
                  </button>
                      <button v-for="(action, idx) in row.actions" :key="'r-'+idx"
                          :class="getRowButtonClass(action)"
                          :style="getCellStyle(header,row)"
                          :title="action.tooltip" :disabled="!action.enabled"
                          @click="triggerRowAction(action, row,
                         buildTableColElementRef(row_index, col_index,'-action-btn-' + action.action))"
                          :id="
                    buildTableColElementRef(
                      row_index,
                      col_index,
                      '-action-btn-' + action.action
                    )
                  "
                          :ref="
                    buildTableColElementRef(
                      row_index,
                      col_index,
                      '-action-btn-' + action.action
                    )
                  "


                  >
                    <i :class="action.icon"></i>
                  </button>
                </div>





              </div>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

      <!-- STICKY POZIOMY SCROLLBAR (zawsze widoczny) -->
      <div class="sticky-hscroll custom-scrollbar"
           ref="stickyHScroll"
           @scroll="syncScrollFromSticky"
      >
        <div class="sticky-hscroll-inner" :style="{ width: tableScrollWidth + 'px' }"></div>
      </div>


    </div>

    <div v-if="showPaginationBottom" class="p-2 border-top bg-light d-flex justify-content-end">
      <b-pagination
          v-model="changedPage"
          size="sm"
          :total-rows="totalRows"
          :per-page="perPage"
          class="m-0"
          aria-controls="tableEl"
          :first-text="changedPage != 1 ? '1' : '||'"
          :last-text="totalPages > 1 ? totalPages.toString() : '||'"
      />
    </div>


    <teleport to="body">
      <div
          v-if="hoverPopupVisible"
          class="cell-hover-popup"
          :style="{ top: hoverPopupTop + 'px', left: hoverPopupLeft + 'px', maxWidth: hoverPopupMaxWidth + 'px' }"
      >
        {{ hoverPopupText }}
      </div>
    </teleport>

  </div>
</template>

<script>
import dateFormat from "dateformat";
import {BAlert, BPagination} from "bootstrap-vue-next";
import TableColumnManagerModal from "@/components/widgets/TableColumnManagerModal.vue";
import Swal from "sweetalert2";

export default {
  name: "CommonTable2",
  props: {
    table: Object, rowIdentifier: String, headerCheckboxClass: String,
    perPage: {type: Number, default: () => 50},
    currentPage: Number,
    totalRows: Number,
    totalPages: Number,
    showPaginationTop: Boolean,
    showPaginationBottom: Boolean,
    initiallyCheckedItemsData: {type: Array, default: () => []},
    disableInitiallyCheckedItems: {type: Boolean, default: () => false},
    ignoreInitiallyCheckedItems: {type: Boolean, default: () => false},
    sortColumn: {type: String, default: () => ""},
    sortDirection: {type: String, default: () => "DESC"},
    multiselect: {type: Boolean, default: true},
    showMassActions: {type: Boolean, default: () => true},
    showRowActions: {type: Boolean, default: () => true},
    showCheckboxColumn: {type: Boolean, default: () => true},
    enableRowSelection: {
      type: Boolean,
      default: () => true
    },
    stickyHeaderTop: { type: Number, default: 0 },
    stickyScrollbarBottom: { type: Number, default: 0 },
    initiallyVisibleColumns: {type: Array, default: () => []},
    exportEnabled: { type: Boolean, default: false }
  },
  components: {TableColumnManagerModal, BAlert, BPagination},
  emits: ["onItemAction", "onSort", "onMassAction", "onCheckItem", "onPageChange", "onSelectRow","onExportTableData", "onVisibleColumnsChange","onEnterRowSelected"],
  data() {
    return {
      localTable: {},
      changedPage: this.currentPage,
      localRowIdentifier: "guid",
      selectedItems: [],
      selectedItemsData: [],
      selectAllItems: false,
      visibleDataItemsCount:0,
      itemsMassActionsEnabled: false,
      showCopyTooltipAlert: false,
      copyTooltipContent: "",
      copySuccess: false,
      copyStatusText: '',
      copyStatusTimeout: null,
      clickListener: null,
      singleSelectedRowId: null,
      showColumnManager:false,
      visibleHeaders:[],
      visibleHeaderKeys:[],
      tableScrollWidth: 0,
      isSyncingScroll: false,
      resizeObserver: null,
      hoverDelayMs: 1000,          // 1000–2000ms
      hoverTimer: null,

      hoverPopupVisible: false,
      hoverPopupText: "",
      hoverPopupTop: 0,
      hoverPopupLeft: 0,
      hoverPopupMaxWidth: 400,
      // Removed complex massOperationsDisplayStyle in favor of Vue Transitions + CSS
    };
  },
  computed: {
    tableData() {
      if (this.localTable != null && this.localTable.data != null) {
        if (this.ignoreInitiallyCheckedItems === true) {
          this.initiallyCheckedItemsData.forEach(item => {
            const index = this.localTable.data.findIndex(row => row[this.localRowIdentifier] === item[this.localRowIdentifier]);
            if (index > -1) {
              this.localTable.data.splice(index, 1);
            }
          });
          return this.localTable.data
        } else {
          return this.localTable.data;
        }
      }
      return [];
    },

    availableHeaders(){
      if(!this.visibleHeaders){
        return [];
      }

      return this.visibleHeaders;
    },

    // tableDataLength(){
    //   return this.localTable.data.length;
    // }
  },
  methods: {
    focusTableContainer() {
      if (this.$refs.tableScroll) {
        this.$refs.tableScroll.focus();
      }
    },

    getSelectableRows() {
      return this.tableData.filter(row => !row.generatedEmptyRow);
    },

    getCurrentSelectedRow() {
      if (!this.singleSelectedRowId) {
        return null;
      }

      return this.getSelectableRows().find(
          row => row[this.localRowIdentifier] === this.singleSelectedRowId
      ) || null;
    },
    selectRowByKeyboard(row) {
      if (!row || row.generatedEmptyRow || this.enableRowSelection === false) {
        return;
      }

      this.singleSelectedRowId = row[this.localRowIdentifier];
      this.$emit("onSelectRow", row);

      this.$nextTick(() => {
        this.scrollSelectedRowIntoView();
      });
    },
    scrollSelectedRowIntoView() {
      if (!this.singleSelectedRowId) {
        return;
      }

      const rowRef = this.buildTableRowRef(this.singleSelectedRowId);
      const rowElementRef = this.$refs[rowRef];

      if (!rowElementRef || !rowElementRef.length || !this.$refs.tableScroll) {
        return;
      }

      const rowEl = rowElementRef[0];
      const container = this.$refs.tableScroll;

      const rowTop = rowEl.offsetTop;
      const rowBottom = rowTop + rowEl.offsetHeight;

      const visibleTop = container.scrollTop;
      const visibleBottom = visibleTop + container.clientHeight;

      if (rowTop < visibleTop) {
        container.scrollTop = rowTop;
      } else if (rowBottom > visibleBottom) {
        container.scrollTop = rowBottom - container.clientHeight;
      }
    },
    handleTableKeydown(event) {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();

      const isTypingElement =
          tagName === "input" ||
          tagName === "textarea" ||
          tagName === "select" ||
          target?.isContentEditable;

      if (isTypingElement) {
        return;
      }


      if (this.enableRowSelection !== true) {
        return;
      }

      const rows = this.getSelectableRows();
      if (!rows.length) {
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();

        if (!this.singleSelectedRowId) {
          this.selectRowByKeyboard(rows[0]);
          return;
        }

        const currentIndex = rows.findIndex(
            row => row[this.localRowIdentifier] === this.singleSelectedRowId
        );

        if (currentIndex === -1) {
          this.selectRowByKeyboard(rows[0]);
          return;
        }

        const nextIndex = Math.min(currentIndex + 1, rows.length - 1);
        this.selectRowByKeyboard(rows[nextIndex]);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();

        if (!this.singleSelectedRowId) {
          this.selectRowByKeyboard(rows[0]);
          return;
        }

        const currentIndex = rows.findIndex(
            row => row[this.localRowIdentifier] === this.singleSelectedRowId
        );

        if (currentIndex === -1) {
          this.selectRowByKeyboard(rows[0]);
          return;
        }

        const prevIndex = Math.max(currentIndex - 1, 0);
        this.selectRowByKeyboard(rows[prevIndex]);
        return;
      }

      if (event.key === "Enter") {
        const selectedRow = this.getCurrentSelectedRow();
        if (!selectedRow) {
          return;
        }

        event.preventDefault();
        this.$emit("onEnterRowSelected", selectedRow);
      }

      if (event.key === " " || event.code === "Space") {
        if (this.showCheckboxColumn !== true) {
          return;
        }

        const selectedRow = this.getCurrentSelectedRow();
        if (!selectedRow) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.toggleSelectedRowCheckbox(selectedRow);
        return;
      }
    },
    toggleSelectedRowCheckbox(row) {

      if (this.isCheckboxDisabled(row)) {
        return;
      }
      if (!row || row.generatedEmptyRow) {
        return;
      }

      if (this.showCheckboxColumn !== true) {
        return;
      }

      const rowId = row[this.localRowIdentifier];
      const alreadyChecked = this.selectedItems.includes(rowId);

      if (this.multiselect === true) {
        if (alreadyChecked) {
          this.selectedItems = this.selectedItems.filter(item => item !== rowId);
          this.updateCheckedItemsData([{ item: rowId, checked: false }]);
        } else {
          this.selectedItems.push(rowId);
          this.updateCheckedItemsData([{ item: rowId, checked: true }]);
        }
      } else {
        if (alreadyChecked) {
          this.selectedItems = [];
          this.selectedItemsData = [];
          this.updateCheckedItemsData([{ item: rowId, checked: false }]);
        } else {
          this.selectedItems = [rowId];
          this.selectedItemsData = [];
          this.updateCheckedItemsData([{ item: rowId, checked: true }]);
        }
      }

      this.toggleItemsMassOperationsButtons();
      this.$emit("onCheckItem", {
        items: this.selectedItems,
        itemsData: this.selectedItemsData,
        count: this.selectedItems.length
      });
    },
    exportTableData(event){
     const payload ={
       visibleHeaders: event.visibleHeaders,
       selectedItems: this.selectedItems,
     }
     this.$emit("onExportTableData",payload);

    },
    appendEmptyRecords(count = 500, headers = null) {
      if (!this.localTable) return [];

      const hdrs = Array.isArray(headers) && headers.length
          ? headers
          : (this.localTable.headers || []);

      if (!hdrs.length) {
        console.warn("[CommonTable2] appendEmptyRecords: Brak headerów.");
        return [];
      }

      // Klucze kolumn
      const keys = hdrs
          .map(h => h?.key ?? h?.KEY)
          .filter(Boolean);

      if (!keys.length) {
        console.warn("[CommonTable2] appendEmptyRecords: Headery bez key/KEY.");
        return [];
      }

      // Upewnij się, że data istnieje
      if (!this.localTable.data || !Array.isArray(this.localTable.data)) {
        this.localTable.data = [];
      }

      const startIndex = this.localTable.data.length;

      // Generowanie pustych rekordów
      const newRows = Array.from({ length: Number(count) || 0 }, (_, i) => {
        const row = { __rowId: startIndex + i + 1 };
        for (const k of keys) row[k] = "";
        row["generatedEmptyRow"] = true;

        return row;
      });

      // DOPIĘCIE zamiast podmiany
      this.localTable.data.push(...newRows);

      // Odśwież szerokość tabeli (sticky h-scroll)
      this.$nextTick(() => {
        if (typeof this.updateTableScrollWidth === "function") {
          this.updateTableScrollWidth();
        }
      });

      return newRows;
    },

    onCellMouseEnter(e) {
      // szukamy elementu, który ma ellipsis
      const el = e.currentTarget?.querySelector?.(".cell-content");
      if (!el) return;

      // jeśli tekst nie jest ucięty (nie ma ellipsy) – nie pokazuj popupu
      const isEllipsed = el.scrollWidth > el.clientWidth;
      if (!isEllipsed) return;

      // kasujemy poprzednie timery (ważne przy szybkim przejeżdżaniu po tabeli)
      clearTimeout(this.hoverTimer);

      this.hoverTimer = setTimeout(() => {
        // ustaw treść popupu
        this.hoverPopupText = el.innerText ?? el.textContent ?? "";

        // pozycjonowanie popupu względem ekranu (fixed)
        const rect = el.getBoundingClientRect();
        const padding = 8;

        let left = rect.left;
        let top = rect.bottom + 6;

        // proste “nie wyjdź poza ekran” (prawo)
        const maxWidth = this.hoverPopupMaxWidth;
        const viewportW = window.innerWidth;
        if (left + maxWidth + padding > viewportW) {
          left = Math.max(padding, viewportW - maxWidth - padding);
        }

        // “nie wyjdź poza dół” – jeśli brakuje miejsca, pokaż nad komórką
        const viewportH = window.innerHeight;
        const estimatedH = 120; // orientacyjnie
        if (top + estimatedH + padding > viewportH) {
          top = rect.top - 6;
        }

        this.hoverPopupLeft = left;
        this.hoverPopupTop = top;
        this.hoverPopupVisible = true;
      }, this.hoverDelayMs);
    },

    onCellMouseLeave() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;

      this.hoverPopupVisible = false;
      this.hoverPopupText = "";
    },


    updateTableScrollWidth() {
      const tableEl = this.$refs.tableEl;
      if (!tableEl) return;
      // scrollWidth = realna szerokość tabeli (wraz z “wyjechaniem” poza ekran)
      this.tableScrollWidth = tableEl.scrollWidth || 0;
    },

    syncScrollFromTable() {
      if (this.isSyncingScroll) return;
      const tableScroll = this.$refs.tableScroll;
      const sticky = this.$refs.stickyHScroll;
      if (!tableScroll || !sticky) return;

      this.isSyncingScroll = true;
      sticky.scrollLeft = tableScroll.scrollLeft;
      requestAnimationFrame(() => (this.isSyncingScroll = false));
    },

    syncScrollFromSticky() {
      if (this.isSyncingScroll) return;
      const tableScroll = this.$refs.tableScroll;
      const sticky = this.$refs.stickyHScroll;
      if (!tableScroll || !sticky) return;

      this.isSyncingScroll = true;
      tableScroll.scrollLeft = sticky.scrollLeft;
      requestAnimationFrame(() => (this.isSyncingScroll = false));
    },


    updateVisibleColumnsWithEmit(newVisibleKeys){
     this.updateVisibleColumns(newVisibleKeys)
      this.$emit("onVisibleColumnsChange", this.visibleHeaders);
    },
    updateVisibleColumns(newVisibleKeys){
      this.visibleHeaderKeys = newVisibleKeys;
      if(!this.localTable.headers) return;
      this.visibleHeaders = this.localTable.headers.filter(header => this.visibleHeaderKeys.includes(header.key));
      this.$nextTick(() => this.updateTableScrollWidth());
    },


    // --- Helpers for Styling ---
    isSortedAsc(key) {
      return this.sortColumn.toLowerCase() === key.toLowerCase() && this.sortDirection === 'ASC';
    },
    isSortedDesc(key) {
      return this.sortColumn.toLowerCase() === key.toLowerCase() && this.sortDirection === 'DESC';
    },

    // --- Existing Logic Preserved ---
    resetCheckedItems() {
      this.selectedItems = [];
      this.selectedItemsData = [];
      this.selectAllItems = false;
      this.toggleItemsMassOperationsButtons();
    },
    isCheckboxDisabled(row) {
      const initialCheckedItemsIds = this.initiallyCheckedItemsData != null ? this.initiallyCheckedItemsData.map(item => item[this.localRowIdentifier]) : [];
      return this.disableInitiallyCheckedItems === true && initialCheckedItemsIds.includes(row[this.localRowIdentifier]);
    },
    selectItem() {
      var checkedEvents = [];
      if (this.selectAllItems) {
        for (let i in this.table.data) {
          const rowId = this.table.data[i][this.rowIdentifier]
          const preventSelectionOfInitiallySelectedItems = this.disableInitiallyCheckedItems === true && this.initiallyCheckedItemsData != null && this.initiallyCheckedItemsData.includes(rowId)
          if (!preventSelectionOfInitiallySelectedItems) {
            if (!this.selectedItems.includes(rowId)) {
              this.selectedItems.push(rowId);
              checkedEvents.push({item: rowId, checked: true});
              this.addRowHighlight(rowId);
            }
          }
        }
      } else {
        for (let i in this.table.data) {
          const rowId = this.table.data[i][this.rowIdentifier]
          const indexOfFoundId = this.selectedItems.indexOf(rowId)
          if (indexOfFoundId > -1) {
            this.selectedItems.splice(indexOfFoundId, 1);
            checkedEvents.push({item: rowId, checked: false});
            this.clearRowHighlight(rowId);
          }
        }
      }
      this.toggleItemsMassOperationsButtons();
      this.triggerCheckAllAction(checkedEvents);
    },
    handleRowClick(row) {
      this.focusTableContainer();
      this.onSelectRow(row);
    },
    onSelectRow(row) {
      if(row.generatedEmptyRow || this.enableRowSelection === false){
        return;
      }

      this.singleSelectedRowId = row[this.localRowIdentifier];
      this.$emit("onSelectRow", row);

    },
    triggerCheckAllAction(checkedEvents) {
      this.updateCheckedItemsData(checkedEvents);
      this.$emit("onCheckItem", {items: this.selectedItems, itemsData: this.selectedItemsData,count: this.selectedItems.length});
    },
    triggerCheckAction(event) {
      const selectedItem = event.target.value;
      const isChecked = event.target.checked;
      const checkEventPayload = {item: selectedItem, checked: isChecked}
      if (this.multiselect === true) {
        this.updateCheckedItemsData([checkEventPayload]);
        this.$emit("onCheckItem", {items: this.selectedItems, itemsData: this.selectedItemsData,count: this.selectedItems.length});
      } else {
        this.selectedItems = [];
        this.selectedItemsData = [];
        this.selectedItems.push(selectedItem);
        this.updateCheckedItemsData([checkEventPayload]);
        this.$emit("onCheckItem", {items: this.selectedItems, itemsData: this.selectedItemsData,count: this.selectedItems.length});
      }
    },
    toggleItemsMassOperationsButtons() {
      // Simplified logic - state is handled by Vue transition in template
      this.itemsMassActionsEnabled = (this.showMassActions === true && this.selectedItems.length > 0 && this.localTable.massActions != null && this.localTable.massActions.length > 0);
    },
    sortBy(header) {
      if (!header.sortable) return;
      this.$emit("onSort", header.key);
    },
    triggerRowAction(action, item, elementRef) {
      this.$emit("onItemAction", {
        action: action,
        itemData: item,
        item: item[this.rowIdentifier],
        elementRef: elementRef
      });
    },
    triggerMassAction(action) {
      this.$emit("onMassAction", {action: action, items: this.selectedItems, itemsData: this.selectedItemsData});
    },
    buildTableColElementRef(rowId, colId, suffix) {
      return "col-id-" + colId + "-row-id-" + rowId + suffix;
    },
    getMassActionClass(action) {
      let baseClass = "btn-sm";
      if (action.class) {
        baseClass += " " + action.class;
      } else {
        baseClass += "btn btn-sm btn-outline-secondary d-inline-flex align-items-center bg-white" //default class
      }
      return baseClass;
    },
    getRowButtonClass(action) {
      let baseClass = "btn-sm";
      if (action.class) {
        baseClass += " " + action.class;
      } else {
        baseClass += 'btn btn-sm text-decoration-none p-0 px-1' //default class
      }
      return baseClass;
    },
    getCellClass(header, row) {
      // header contentClass has priority , because it refers to particular column in particular row
      if (header.contentClass != null) {
        return header.contentClass + "";
      }

      if (row["row-class"] != null) {
        return row["row-class"] + "";
      }

      return "";
    },
    getCellStyle(header, row) {
      // row style has priority before header style
      if (row["row-style"] != null) {
        return row["row-style"];
      }

      if (header.style != null) {
        return header.style;
      }

    },

    buildTableColRef(rowId, colId) {
      return "col-id-" + colId + "-row-id-" + rowId;
    },
    updateCheckedItemsData(checkedEvents) {
      for (let i in checkedEvents) {
        var checkedItem = checkedEvents[i].item;
        if (checkedItem) {
          checkedItem = checkedItem.toString();
        }
        const isChecked = checkedEvents[i].checked;
        var selectedItemData = this.table.data.find(row => row[this.rowIdentifier].toString() === checkedItem);
        if (selectedItemData == null) continue;
        if (isChecked) {
          this.selectedItemsData.push(selectedItemData);
        } else {
          const indexOfFoundId = this.selectedItemsData.findIndex(item => item[this.rowIdentifier].toString() === selectedItemData[this.rowIdentifier].toString());
          if (indexOfFoundId > -1) this.selectedItemsData.splice(indexOfFoundId, 1);
        }
      }
    },
    getRowClass(row) {
      let css = "";
      if (row['row-class'] != null) css += row['row-class'];

      // Logic for multiselect checkbox (istniejące)
      if (this.selectedItems.includes(row[this.localRowIdentifier])) {
        if (css.includes("row-disabled")) return "row-disabled";
        css += " row-highlight";
      }

      if (!this.enableRowSelection) {
        return css;
      }

      // Logic for single row selection
      if (this.singleSelectedRowId === row[this.localRowIdentifier]) {
        css += " row-selected"; // Dodajemy klasę CSS dla ramki
      }

      return css;
    },

    addRowHighlight(rowId) {
      var rowRef = this.buildTableRowRef(rowId);
      var rowElement = this.$refs[rowRef];
      if (rowElement != null && rowElement.length > 0) {
        if (rowElement[0].className == null) {
          rowElement[0].className = "row-highlight";
        } else {
          if (!rowElement[0].className.includes("row-highlight") && !rowElement[0].className.includes("row-disabled")) {
            rowElement[0].className = rowElement[0].className + " row-highlight";
          }
        }
      }
    },

    clearRowHighlight(rowId) {
      var rowRef = this.buildTableRowRef(rowId);
      var rowElement = this.$refs[rowRef];
      if (rowElement != null && rowElement.length > 0) {
        rowElement[0].className = rowElement[0].className.replace("row-highlight", "");
      }
    },

    clearRowHighlightForAllItems() {
      for (let i in this.table.data) {
        const rowId = this.table.data[i][this.rowIdentifier]
        this.clearRowHighlight(rowId);
      }
    },


    // Ref helpers
    buildTableElementId(p, c, r, s) {
      return p + c + r + s;
    },
    buildTableRowRef(v) {
      return "row-id-" + v;
    },
    getHeaderStyle(header) {
      let styleObject = {};

      // 1. Jeśli w JSON podano 'width', dodaj to jako styl
      if (header.width) {
        styleObject.width = header.width;
      }

      // 2. Jeśli w JSON podano 'style' (string), dołącz go
      // Uwaga: To prosta obsługa. Jeśli header.style jest stringiem "color: red;",
      // Vue potrafi obsłużyć tablicę stylów [obj, string] w :style.

      // Zwracamy tablicę: [ { width: ... }, "ewentualne-inne-style" ]
      return [styleObject, header.style];
    },

    getDateTime(sourceValue, isEpochMilliseconds) {
      if (!isEpochMilliseconds) {
        return sourceValue;
      }

      if (!sourceValue || sourceValue == 0) {
        return "Brak daty";
      }

      var date = new Date(sourceValue); // The 0 sets the date to the epoch
      return dateFormat(date, "dd.mm.yyyy HH:MM:ss");

    },
    getDate(sourceValue, isEpochMilliseconds) {
      if (!isEpochMilliseconds) {
        return sourceValue;
      }

      if (!sourceValue || sourceValue == 0) {
        return "Brak daty";
      }

      var date = new Date(sourceValue); // The 0 sets the date to the epoch
      return dateFormat(date, "dd.mm.yyyy");

    },


    async handleCellDoubleClick(event, value, header) {
      if (header == null || header.preventCopying === true) return;
      event.preventDefault();
      this.clearTextSelection();
      this.showCopyTooltip(event, value, header.key);
      try {
        await this.copyToClipboard(value);
      } catch (error) {
        this.copyTooltipContent = 'Kopiowanie nie powiodło się!';
      }
      if (this.copyStatusTimeout) clearTimeout(this.copyStatusTimeout);
      this.copyStatusTimeout = setTimeout(() => {
        this.copyTooltipContent = '';
        this.showCopyTooltipAlert = false;
      }, 5000);
    },
    clearTextSelection() {
      if (window.getSelection) window.getSelection().removeAllRanges();
      else if (document.selection) document.selection.empty();
    },
    async copyToClipboard(text) {
      await navigator.clipboard.writeText(text);
    },
    showCopyTooltip(event, value, key) {
      this.copyTooltipContent = key === 'number' ? this.formatCurrency(value) : value;
      if (this.copyTooltipContent && this.copyTooltipContent.toString().length > 0) {
        this.showCopyTooltipAlert = true;
      }
      if (!this.clickListener) {
        this.clickListener = (e) => {
          if (!e.target.closest('.copy-tooltip-wrapper')) {
            this.copyStatusText = '';
          }
        };
        document.addEventListener('dblclick', this.clickListener);
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('pl-PL', {style: 'currency', currency: 'PLN'}).format(amount);
    },
    getCellContentClass(header){
      return "cell-content";//header.cellHover ? "cell-content" : "cell-content-without-hover";
    },
    handleContainerClick(event) {
      if (!event.target.closest('.copy-tooltip-wrapper')) {
        if (this.copyTooltipContent && this.copyTooltipContent.length > 0) {
          this.showCopyTooltipAlert = true;
        }
      }
    },
    handleGlobalKeydown(event) {
      if (event.ctrlKey && event.key.toLowerCase() === "a") {
        event.preventDefault();
        this.selectAllItems = true;
        this.selectItem()
      }
    },
    getProgressColor(value, header) {
      const val = parseFloat(value) || 0;
      // Przykład użycia Custom Ranges w headerze:
      // {
      //   "text": "Poziom Ryzyka",
      //     "key": "risk",
      //     "type": "progress",
      //     "width": "150px",
      //     "ranges": [
      //   { "max": 20, "color": "#28a745" },  // Poniżej 20% - Zielony (Małe ryzyko)
      //   { "max": 50, "color": "#17a2b8" },  // Poniżej 50% - Niebieski
      //   { "max": 80, "color": "#fd7e14" }   // Poniżej 80% - Pomarańczowy
      // ],
      //     "defaultColor": "#dc3545"             // Powyżej 80% - Czerwony (Krytyczne)
      // }

      // 1. Obsługa Custom Ranges (jeśli zdefiniowane w headerze)
      // Oczekiwana struktura w headerze:
      // ranges: [ { max: 30, color: '#dc3545' }, { max: 60, color: 'orange' } ], defaultColor: 'green'
      if (header.ranges && Array.isArray(header.ranges)) {
        // Sortujemy rosnąco po 'max', żeby logikę sprawdzać po kolei
        const sortedRanges = [...header.ranges].sort((a, b) => a.max - b.max);

        for (const range of sortedRanges) {
          if (val < range.max) {
            return range.color;
          }
        }
        // Jeśli wartość przekracza wszystkie zakresy, zwracamy kolor domyślny lub zielony
        return header.defaultColor || '#198754';
      }

      // 2. Domyślna logika (Hardcoded Standard)
      // < 30% -> Czerwony (Bootstrap Danger)
      if (val < 30) return '#dc3545';

      // < 60% -> Żółty (Bootstrap Warning)
      if (val < 60) return '#ffc107';

      // >= 60% -> Zielony (Bootstrap Success)
      return '#198754';
    },
    handleClickOutside(event) {
      // Sprawdzamy, czy kliknięty element (event.target) NIE znajduje się wewnątrz komponentu (this.$el)
      if (this.$el && !this.$el.contains(event.target)) {
        // Jeśli kliknięto poza tabelą/komponentem -> odznaczamy wiersz
        this.singleSelectedRowId = null;
        this.$emit("onSelectRow", {});
      }
    },
  },
  watch: {
    selectedItems: {
      handler() {
        this.toggleItemsMassOperationsButtons();
      },
      deep: true,
    },
    changedPage: {
      handler() {
        this.$emit("onPageChange", this.changedPage);
      },
      deep: true,
    },
  },
  created() {
    this.localTable = JSON.parse(JSON.stringify(this.table));
    this.visibleHeaders = this.localTable.headers;
    if (this.rowIdentifier != null) this.localRowIdentifier = this.rowIdentifier;
    if (this.ignoreInitiallyCheckedItems === true) {
      this.selectedItemsData = [];
      this.selectedItems = [];
    } else {
      this.selectedItemsData = JSON.parse(JSON.stringify(this.initiallyCheckedItemsData));
      this.selectedItems = this.initiallyCheckedItemsData.map(item => item[this.localRowIdentifier]);
      for (let i in this.selectedItems) {
        this.addRowHighlight(this.selectedItems[i]);
      }

    }

    const dataRecordsCount = this.localTable.data ? this.localTable.data.length : 0 ;
    const pageSize = this.perPage
    var emptyRecordsToGenerate = pageSize - dataRecordsCount;
    if (emptyRecordsToGenerate < 0) {
      emptyRecordsToGenerate = 0; // just in case
    }
    this.visibleDataItemsCount = dataRecordsCount;
    const initiallyCheckedItemsCount = this.initiallyCheckedItemsData.length
    if(this.disableInitiallyCheckedItems){
      this.visibleDataItemsCount = dataRecordsCount - initiallyCheckedItemsCount;
    }
    if(this.visibleDataItemsCount > 0){
      this.appendEmptyRecords(emptyRecordsToGenerate);
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateTableScrollWidth);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.clickListener) document.removeEventListener('dblclick', this.clickListener);
    if (this.copyStatusTimeout) clearTimeout(this.copyStatusTimeout);
    window.removeEventListener("keydown", this.handleGlobalKeydown);
    // cleaning up clicking out of table click listener
    // document.removeEventListener('click', this.handleClickOutside);
  },
  mounted() {
    if(this.initiallyVisibleColumns && this.initiallyVisibleColumns.length > 0){
      this.updateVisibleColumns(this.initiallyVisibleColumns);
    }



    this.$nextTick(() => {
      this.updateTableScrollWidth();

      // obserwuj zmiany rozmiaru tabeli (kolumny widoczne/ukryte, dane, resize okna)
      const tableEl = this.$refs.tableEl;
      if (tableEl && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateTableScrollWidth());
        this.resizeObserver.observe(tableEl);
      }

      window.addEventListener("resize", this.updateTableScrollWidth, { passive: true });
      window.addEventListener("keydown", this.handleGlobalKeydown);
    });
  }
};
</script>

<style scoped>

.table-section{
  height: 512px;
}

/* Modern System Font Stack */
.common-table-card {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif;
  font-size: 0.875rem; /* 14px */
}

/* Custom Scrollbar for the table container */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ced4da; /* Bootstrap Gray-400 */
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #adb5bd; /* Bootstrap Gray-500 */
}
.custom-scrollbar::-webkit-scrollbar {
  width: 12px; /* Nieco szerszy dla wygody */
  height: 12px; /* Wysokość poziomego paska */
}


/* --- TABELA --- */
.modern-table {
  border-collapse: separate; /* Wymagane dla sticky headers + borders */
  border-spacing: 0;

  /* Fixed pozwala precyzyjnie sterować szerokością kolumn */
  table-layout: fixed;

  /* ZMIANA KLUCZOWA: Tabela ma szerokość wynikającą z zawartości/kolumn, a nie kontenera.
     Dzięki temu wyjedzie poza ekran (scroll poziomy) zamiast ściskać kolumny. */
  width: max-content !important;

  /* Ale nie może być węższa niż kontener */
  min-width: 100%;

  margin-bottom: 0;
  border-bottom: 1px solid #dee2e6 !important;
}

/* ZMIANA: Upewniamy się, że tło wierszy przykrywa siatkę pod spodem */
.modern-table tbody tr {
  background-color: #fff;
}

/* --- NAGŁÓWKI I KOMÓRKI --- */
.modern-table th,
.modern-table td {
  /* ZMIANA: Domyślna minimalna szerokość kolumny.
     Jeśli w tableConfig nie podasz width, kolumna nie ściśnie się do zera. */


  /* Box-sizing ułatwia obliczanie paddingów */
  box-sizing: border-box;
}

/* Stylizacja nagłówka sticky */
.modern-table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
  letter-spacing: 0.02em;
  overflow: hidden;
  white-space: nowrap;
  vertical-align: middle;

  /* Fix dla sticky wewnątrz scrollable container */
  position: sticky;
  top: var(--ct-sticky-top, 0px);
  z-index: 20;
  background-color: #f8f9fa; /* ważne przy sticky */
  /* Border pod nagłówkiem musi być widoczny */
  box-shadow: 0 1px 0 #dee2e6;
}

/* Stylizacja komórek */
.modern-table td {
  color: #212529;
  border-bottom: 1px solid #dee2e6 !important;
}

/* Row Active State (Better than default Bootstrap) */
tr.row-highlight > td {
  background-color: #f0f8ff !important; /* Nadpisuje striped/hover */

}

tr.row-highlight > td:first-child {
  box-shadow: inset 2px 0 0 #1e90ff;
}


.row-active-indicator {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #0d6efd; /* Primary Blue */
}

/* Disabled Row */
tr.row-disabled {
  background-color: #f8f9fa !important;
  color: #adb5bd !important;
  cursor: not-allowed;
  opacity: 0.7;
}



tr.row-warning {
  background-color: #fff3cd !important;
  color: #856404 !important;
}

tr.row-warning > td {
  background-color: #fff3cd !important;
}

tr.row-warning > td:first-child {
  box-shadow: inset 2px 0 0 #1e90ff;
}

/* Tooltip Wrapper */
.copy-tooltip-wrapper {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1060; /* Higher than Bootstrap Modals */
  max-width: 300px;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Utilities */
.cursor-pointer {
  cursor: pointer;
}

/* Bootstrap 5-like utilities if missing in project environment */
.bg-success-subtle {
  background-color: #d1e7dd;
}

.text-success-emphasis {
  color: #0a3622;
}

.bg-danger-subtle {
  background-color: #f8d7da;
}

.text-danger-emphasis {
  color: #58151c;
}

/* Sort arrows visual adjustment */
.sort-indicators i {
  height: 6px;
  line-height: 6px;
  opacity: 0.3;
}

.sort-indicators i.text-dark {
  opacity: 1;
}

/* Cell Content Ellipsis Helper */


.cell-content-without-hover{
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  width: 100%;
  cursor: pointer;
}

.cell-content {
  /* Te 3 linie robią "..." */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Resetowanie zachowania bloku */
  display: block;
  width: 100%;
  cursor: pointer;
}


.col-number {
  width: 88px; /* Width for number column */
}

.col-currency {
  width: 120px; /* Width for currency column */
}

.col-mid-text {
  width: 148px; /* Width for number column */
}

.col-name {
  width: 308px; /* Width for name column */
}

.col-date {
  width: 90px; /* Width for date column */
}

/* --- STYL DLA ZAZNACZONEGO WIERSZA (Single Selection Border) --- */

/* 1. Nadajemy grubsze krawędzie GÓRNE i DOLNE wszystkim komórkom w wierszu */
.modern-table tr.row-selected > td {
  /* Kolor #0d6efd to Bootstrap Primary Blue. 2px to "grubsza ramka" */
  border-top: 2px solid #0d6efd !important;
  border-bottom: 2px solid #0d6efd !important;

  /* Opcjonalnie: zmiana tła na bardzo delikatny niebieski, żeby wyróżnić aktywny wiersz */
  background-color: rgba(13, 110, 253, 0.1) !important;
}

/* 2. Nadajemy LEWĄ krawędź tylko PIERWSZEJ komórce */
.modern-table tr.row-selected > td:first-child {
  border-left: 2px solid #0d6efd !important;
}

/* 3. Nadajemy PRAWĄ krawędź tylko OSTATNIEJ komórce */
.modern-table tr.row-selected > td:last-child {
  border-right: 2px solid #0d6efd !important;
}

/* 4. Fix: Jeśli wiersz jest zaznaczony, usuwamy standardowy border-bottom sąsiada powyżej,
   żeby nasza gruba ramka nie kolidowała (kosmetyka) */
.modern-table tr.row-selected + tr > td {
  border-top: 0 !important;
}

.table-wrapper{
  position: relative;
  --ct-sticky-top: 0px;
  --ct-sticky-bottom: 0px;
}

.table-scroll::-webkit-scrollbar:horizontal {
  height: 0 !important;
}
.table-scroll {
  scrollbar-gutter: stable; /* opcjonalnie */
}
.table-scroll {
  scrollbar-width: none;      /* Firefox (ukryje też pionowy) */
  -ms-overflow-style: none;   /* stary Edge/IE */
}
.table-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* sticky poziomy pasek */
.sticky-hscroll{
  position: sticky;
  bottom: var(--ct-sticky-bottom, 0px);
  height: 14px;             /* “grubość” paska */
  overflow-x: auto;
  overflow-y: hidden;
  background: #fff;
  z-index: 50;
  border-top: 1px solid #dee2e6;
}

/* “sztuczna” zawartość, żeby pojawił się scrollbar */
.sticky-hscroll-inner{
  height: 1px;
}

.cell-hover-popup {
  position: fixed;
  z-index: 999999;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);

  white-space: normal;
  pointer-events: none; /* żeby nie gubić hover */
}

.element-not-visible {
  display: none;
}


.icon-center {
  text-align: center;
  font-size: 1rem;
}

.icon-success {
  color: #28a745;
}

.icon-warning {
  color: #f8d47d;
}

.icon-error {
  color: #dc3545;
}

.icon-info {
  color: #17a2b8;
}



</style>